import React, { useState, useEffect } from "react";
// Importing some mock data icons to match the UI style
import { AiOutlinePlus, AiOutlineSearch, AiOutlineEnvironment, AiOutlineClockCircle, AiOutlineEdit, AiOutlineDelete, AiOutlineCloseCircle, AiOutlineCheckCircle } from 'react-icons/ai';
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

const JobListingManager = () => {
    // Use primary color from the original screenshot
    const orange = "#FF6B35";

    // --- MOCK DATA FOR LISTING ---
    // Replace this with actual API calls in production
    const initialJobListings = [
        {
            _id: "job1",
            title: "Senior Plumber",
            description: "Expert needed for high-end residential projects.",
            location: "New York, USA",
            jobType: "full-time",
            skills: ["plumbing", "hvac", "pipefitting"],
            experience: "5+ years",
            jobStatus: "open",
            updateStatus: "applied",
            createdAt: new Date()
        },
        {
            _id: "job2",
            title: "Mid-Level Software Engineer",
            description: "Focus on React and Node.js development.",
            location: "San Francisco, USA",
            jobType: "internship",
            skills: ["react", "javascript", "nodejs"],
            experience: "1-2 years",
            jobStatus: "closed",
            updateStatus: "rejected",
            createdAt: new Date()
        },
    ];

    const [openDropdown, setOpenDropdown] = useState(null);
    const [showApplicants, setShowApplicants] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [applicants, setApplicants] = useState([]);   
    const [jobListings, setJobListings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editingJob, setEditingJob] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [jobToDelete, setJobToDelete] = useState(null);

    // Form State
    const [skillInput, setSkillInput] = useState("");
    const [formData, setFormData] = useState({
        title: "",
        description: "",
        location: "",
        jobType: "full-time",
        skills: [],
        experience: "",
        jobStatus: "open",
    });

    // Handle Input Changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        fetchJobs();
    }, []);

    const fetchJobs = async () => {
        try {
            setLoading(true);

            const res = await makeRequestAuth("get", "/admin/getJobs");

            if (!res?.error) {
                setJobListings(res.data || []);
            } else {
                showErrorToast(res.message || "Failed to load jobs ❌");
            }

        } catch (err) {
            console.error("Fetch Jobs Error:", err);
            showErrorToast("Something went wrong ❌");
        } finally {
            setLoading(false);
        }
    };

    // Open form for CREATE
    const openCreateForm = () => {
        setEditingJob(null);
        setFormData({
            title: "", description: "", location: "", jobType: "full-time", skills: [], experience: "", jobStatus: "open",
        });
        setIsFormVisible(true);
    };

    // Open form for UPDATE
    const openUpdateForm = (job) => {
        setEditingJob(job._id);
        setFormData({
            ...job,
            skills: job.skills || [], // Convert array to string for easy editing
        });
        setIsFormVisible(true);
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingJob) {
                const res = await makeRequestAuth("put", "/admin/updateJob", {
                    id: editingJob,
                    title: formData.title,
                    description: formData.description,
                    location: formData.location,
                    jobType: formData.jobType,
                    skills: formData.skills,
                    experience: formData.experience,
                    jobStatus: formData.jobStatus
                });

                if (!res?.error) {
                    await fetchJobs(); // 🔥 refresh
                    showSuccessToast("Job Updated Successfully ✅");
                } else {
                    showErrorToast(res.message || "Update failed ❌");
                }
            } else {
                const res = await makeRequestAuth("post", "/admin/createJob", {
                    title: formData.title,
                    description: formData.description,
                    location: formData.location,
                    jobType: formData.jobType,
                    skills: formData.skills,
                    experience: formData.experience
                });

                if (!res?.error) {
                    await fetchJobs();
                    showSuccessToast("Job Created Successfully ✅");
                } else {
                    showErrorToast(res.message || "Error creating job ❌");
                }
            }

            // Reset form
            setEditingJob(null);
            setIsFormVisible(false);

        } catch (err) {
            console.error("Create Job Error:", err);

            // 🔥 Safe error handling
            showErrorToast(
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong ❌"
            );
        }
    };

    // Handle DELETE
    const openDeleteConfirm = (id) => {
        setJobToDelete(id);
        setIsDeleteModalOpen(true);
    };
    const confirmDelete = async () => {
        if (!jobToDelete) return;

        try {
            const res = await makeRequestAuth("delete", "/admin/deleteJob", { id: jobToDelete });

            if (!res?.error) {
                showSuccessToast("Job Deleted Successfully ✅");
                await fetchJobs();
            } else {
                showErrorToast(res.message || "Delete failed ❌");
            }
        } catch (err) {
            showErrorToast("Something went wrong ❌");
        } finally {
            setIsDeleteModalOpen(false);
            setJobToDelete(null);
        }
    };

    const addSkill = () => {
        if (!skillInput.trim()) return;

        setFormData(prev => ({
            ...prev,
            skills: [...prev.skills, skillInput.trim()]
        }));

        setSkillInput("");
    };

    const removeSkill = (index) => {
        setFormData(prev => ({
            ...prev,
            skills: prev.skills.filter((_, i) => i !== index)
        }));
    };

    // Helper function to get status label color (matching Pending/View CV style)
    const getStatusColor = (status) => {
        switch (status) {
            case 'open': case 'applied': return orange;
            case 'closed': case 'rejected': return '#F05252'; // A reddish color
            case 'hired': return '#10B981'; // A greenish color
            default: return '#718096';
        }
    };

    const handleJobStatusChange = async (jobId, newStatus) => {
    setOpenDropdown(null);
    const res = await makeRequestAuth("post", "/admin/updateJobStatus", { id: jobId, jobStatus: newStatus });
    if (!res?.error) {
        showSuccessToast("Job status updated ✅");
        fetchJobs();
    } else {
        showErrorToast("Failed to update status ❌");
    }
};

const fetchApplicants = async (jobId) => {
    const res = await makeRequestAuth("post", "/admin/getCareerListing", { pageNo: 1, size: 50 });
    if (!res?.error) {
        const filtered = res.data.list.filter(a => a.jobId === jobId);
        setApplicants(filtered);
    }
};


    const renderJobMeta = (job) => {
        const iconStyle = { color: "#718096", marginRight: '4px' };
        const textStyle = { color: "#718096", fontSize: '0.8rem' };
        const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1).replace('-', ' ');

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <AiOutlineClockCircle style={iconStyle} />
                    <span style={textStyle}>{capitalize(job.jobType)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ ...textStyle, fontWeight: 'bold' }}>Exp: {job.experience}</span>
                </div>
            </div>
        );
    };

    // Main UI Render
    return (
        <div style={{ padding: "24px", fontFamily: "Arial, sans-serif" }}>
            {/* --- Page Header Section --- */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: "32px" }}>
                <div>
                    <span style={{ color: "#718096", fontSize: "14px", fontWeight: "bold" }}>ADMIN PANEL</span>
                    <h2 style={{ fontSize: "28px", fontWeight: "bold", color: "#111827", marginTop: "4px" }}>Job Postings</h2>
                    <p style={{ color: "#718096", marginTop: "2px" }}>Create and manage all your job listings here.</p>
                </div>

                {/* Search and Add Buttons */}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ position: "relative" }}>
                        <input
                            type="text"
                            placeholder="Search by job title or location..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: "320px",
                                padding: "10px 16px 10px 40px",
                                border: "1px solid #D1D5DB",
                                borderRadius: "8px",
                                fontSize: "14px",
                                outline: 'none',
                            }}
                        />
                        <AiOutlineSearch style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9CA3AF" }} />
                    </div>
                    <button
                        onClick={openCreateForm}
                        style={{
                            backgroundColor: "#24ADD7", color: "white", padding: "10px 20px", borderRadius: "8px",
                            border: "none", cursor: "pointer", fontWeight: "bold", fontSize: "14px", display: 'flex', alignItems: 'center', gap: '6px'
                        }}>
                        <AiOutlinePlus /> New Job Post
                    </button>
                </div>
            </div>

            <div style={{ display: "flex", gap: "24px", alignItems: "start" }}>

                <div style={{ flex: 2, background: "white", padding: "16px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
                    {jobListings.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: '#718096' }}>No job postings found.</div>
                    ) : (
                        <>
                            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 0.5fr", gap: "10px", padding: "10px 0", color: "#718096", fontSize: "12px", fontWeight: "bold", textTransform: "uppercase" }}>
                                <span>JOB INFO</span>
                                <span>TYPE / EXPERIENCE</span>
                                <span>JOB STATUS</span>
                                <span>UPDATE STATUS</span>
                                <span style={{ textAlign: 'right' }}>ACTIONS</span>
                            </div>

                            {jobListings.map(job => (
                                <div key={job._id} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr 0.5fr", gap: "10px", padding: "16px 0", borderBottom: "1px solid #E5E7EB", alignItems: "center" }}>

                                    <div>
                                        <div style={{ fontWeight: "bold", color: "#111827", marginBottom: '4px' }}>{job.title}</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#718096', fontSize: '0.9rem' }}>
                                            <AiOutlineEnvironment /> {job.location}
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                                            {job.skills?.map(skill => (
                                                <span key={skill} style={{ backgroundColor: '#EDF2F7', color: '#4A5568', padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem' }}>
                                                    {skill}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        {renderJobMeta(job)}
                                    </div>

                                    <div style={{ textAlign: "center" }}>
                                        <span style={{
                                            backgroundColor: orange + '1A',
                                            color: orange,
                                            padding: "4px 10px",
                                            borderRadius: "6px",
                                            fontWeight: "bold",
                                            fontSize: "10px",
                                            textTransform: "uppercase",
                                            border: `1px solid ${orange}`
                                        }}>
                                            {job.jobStatus}
                                        </span>
                                    </div>

                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {job.updateStatus === 'applied' && <AiOutlineClockCircle style={{ color: getStatusColor(job.updateStatus) }} />}
                                            {job.updateStatus === 'hired' && <AiOutlineCheckCircle style={{ color: getStatusColor(job.updateStatus) }} />}
                                            {job.updateStatus === 'rejected' && <AiOutlineCloseCircle style={{ color: getStatusColor(job.updateStatus) }} />}
                                            <span style={{
                                                color: getStatusColor(job.updateStatus),
                                                fontWeight: 'bold',
                                                textTransform: 'capitalize',
                                                fontSize: '0.9rem'
                                            }}>
                                                {job.updateStatus}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#718096', marginTop: '4px' }}>
                                            Created: {job.createdAt}
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", justifyContent: 'flex-end', gap: '10px' }}>
                                        <button onClick={() => openUpdateForm(job)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4A5568' }}><AiOutlineEdit /></button>
                                        <button onClick={() => openDeleteConfirm(job._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F05252' }}><AiOutlineDelete /></button>
                                        {/* A dropdown, styled like the original Actions dropdown */}
                                       
<div style={{ position: 'relative' }}>
    <button
        onClick={() => setOpenDropdown(openDropdown === job._id ? null : job._id)}
        style={{ fontSize: '10px', fontWeight: 'bold', border: '1px solid #D1D5DB', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', background: 'white' }}
    >
        Actions ▼
    </button>

    {openDropdown === job._id && (
        <div style={{ position: 'absolute', right: 0, top: '100%', background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 10, minWidth: '160px' }}>
            <div onClick={() => { setSelectedJob(job); setShowApplicants(true); setOpenDropdown(null); }}
                style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #F3F4F6' }}>
                👁️ View Applicants
            </div>
            <div onClick={() => handleJobStatusChange(job._id, job.jobStatus === 'open' ? 'closed' : 'open')}
                style={{ padding: '8px 16px', cursor: 'pointer', fontSize: '13px', color: job.jobStatus === 'open' ? '#F05252' : '#10B981' }}>
                {job.jobStatus === 'open' ? '🔒 Close Job' : '✅ Open Job'}
            </div>
        </div>
    )}
</div>

                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>

                {/* --- Sidebar (Create/Edit Form) --- */}
                {isFormVisible && (
                    <div style={{ flex: 1, background: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", border: `1px solid #24ADD7` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                            <h3 style={{ fontSize: "18px", fontWeight: "bold" }}>{editingJob ? "Edit Job Posting" : "Add New Job Posting"}</h3>
                            <button onClick={() => setIsFormVisible(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#F05252", fontSize: '18px' }}><AiOutlineCloseCircle /></button>
                        </div>

                        <form onSubmit={handleFormSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                            {/* Common input styling, reusing original colors */}
                            {[
                                { label: "Title", name: "title", type: "text" },
                                { label: "Description", name: "description", type: "text" },
                                { label: "Location", name: "location", type: "text" },
                                { label: "Experience Required", name: "experience", type: "text" },
                            ].map(field => (
                                <div key={field.name}>
                                    <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "bold", color: "#374151" }}>{field.label}</label>
                                    <input
                                        type={field.type} name={field.name} value={formData[field.name]} placeholder={field.placeholder} onChange={handleInputChange} required
                                        style={{ width: "100%", padding: "10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "14px" }}
                                    />
                                </div>
                            ))}

                            <div>
                                <label style={{ fontWeight: "bold" }}>Skills</label>

                                {/* Input + Add button */}
                                <div style={{ display: "flex", gap: "8px", marginTop: "5px" }}>
                                    <input
                                        type="text"
                                        value={skillInput}
                                        onChange={(e) => setSkillInput(e.target.value)}
                                        placeholder="Enter skill"
                                        style={{
                                            flex: 1,
                                            padding: "10px",
                                            border: "1px solid #D1D5DB",
                                            borderRadius: "6px"
                                        }}
                                    />

                                    <button
                                        type="button"
                                        onClick={addSkill}
                                        style={{
                                            backgroundColor: "#24ADD7",
                                            color: "#fff",
                                            border: "none",
                                            padding: "10px 12px",
                                            borderRadius: "6px",
                                            cursor: "pointer"
                                        }}
                                    >
                                        Add
                                    </button>
                                </div>

                                {/* Skills list */}
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "10px" }}>
                                    {formData.skills.map((skill, index) => (
                                        <span
                                            key={index}
                                            style={{
                                                backgroundColor: "#EDF2F7",
                                                padding: "5px 10px",
                                                borderRadius: "6px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "6px"
                                            }}
                                        >
                                            {skill}
                                            <span
                                                onClick={() => removeSkill(index)}
                                                style={{ cursor: "pointer", color: "red" }}
                                            >
                                                ✕
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Specific Enum/Select Fields */}
                            {[
                                { label: "Job Type", name: "jobType", options: ["full-time", "part-time", "internship"] },
                            ].map(field => (
                                <div key={field.name}>
                                    <label style={{ display: "block", marginBottom: "4px", fontSize: "14px", fontWeight: "bold", color: "#374151" }}>{field.label}</label>
                                    <select
                                        name={field.name} value={formData[field.name]} onChange={handleInputChange} required
                                        style={{ width: "100%", padding: "10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "14px", textTransform: 'capitalize' }}
                                    >
                                        {field.options.map(option => <option key={option} value={option} style={{ textTransform: 'capitalize' }}>{option.replace('-', ' ')}</option>)}
                                    </select>
                                </div>
                            ))}

                            <button
                                type="submit"
                                style={{ backgroundColor: "#24ADD7", color: "white", padding: "12px", borderRadius: "8px", border: "none", cursor: "pointer", fontWeight: "bold", fontSize: "16px", marginTop: "12px" }}>
                                {editingJob ? "Update Job Post" : "Add Job Post"}
                            </button>
                        </form>
                    </div>
                )}
                {isDeleteModalOpen && (
                    <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0,0,0,0.6)", // 👈 darker background
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 999
                    }}>
                        <div style={{
                            background: "#fff",
                            padding: "30px",
                            borderRadius: "14px",
                            width: "340px",
                            textAlign: "center",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.3)"
                        }}>

                            {/* ICON */}
                            <div style={{ fontSize: "50px", marginBottom: "10px" }}>
                                🗑️
                            </div>

                            <h3 style={{ marginBottom: "8px" }}>Delete Confirm</h3>

                            <p style={{ color: "#6B7280", marginBottom: "20px" }}>
                                Are you sure you want to delete this??
                            </p>

                            {/* BUTTONS */}
                            <div style={{
                                display: "flex",
                                justifyContent: "center",
                                gap: "15px" // 👈 spacing fix
                            }}>
                                <button
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    style={{
                                        padding: "10px 18px",
                                        borderRadius: "8px",
                                        border: "1px solid #ccc",
                                        background: "#fff",
                                        cursor: "pointer",
                                        fontWeight: "bold"
                                    }}
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={confirmDelete}
                                    style={{
                                        padding: "10px 18px",
                                        borderRadius: "8px",
                                        border: "none",
                                        background: "#F05252",
                                        color: "#fff",
                                        cursor: "pointer",
                                        fontWeight: "bold"
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* === APPLICANTS MODAL === */}
            {showApplicants && selectedJob && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
                    justifyContent: 'center', alignItems: 'center', zIndex: 999
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '14px', width: '700px',
                        maxHeight: '80vh', overflowY: 'auto', padding: '30px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                    }}>
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                👁️ Applicants — {selectedJob.title}
                            </h3>
                            <button onClick={() => { setShowApplicants(false); setApplicants([]); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#F05252' }}>
                                ✕
                            </button>
                        </div>

                        {/* Load Button */}
                        <button onClick={() => fetchApplicants(selectedJob._id)}
                            style={{ backgroundColor: '#24ADD7', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', marginBottom: '16px' }}>
                            🔄 Load Applicants
                        </button>

                        {/* Applicants List */}
                        {applicants.length === 0 ? (
                            <p style={{ color: '#718096', textAlign: 'center', padding: '20px' }}>No applicants yet. Click "Load Applicants".</p>
                        ) : (
                            applicants.map((a, i) => (
                                <div key={i} style={{ padding: '12px', border: '1px solid #E5E7EB', borderRadius: '8px', marginBottom: '10px' }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '15px', marginBottom: '4px' }}>{a.name}</div>
                                    <div style={{ color: '#718096', fontSize: '13px' }}>{a.email} | {a.phone}</div>
                                    <div style={{ color: '#718096', fontSize: '13px', marginTop: '2px' }}>Position: {a.position}</div>
                                    {a.resume && (
                                        <a href={a.resume} target="_blank" rel="noreferrer"
                                            style={{ color: '#24ADD7', fontSize: '13px', display: 'inline-block', marginTop: '6px' }}>
                                            📄 View Resume
                                        </a>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

        </div>
    );
};

export default JobListingManager;